import React, { useEffect, useRef, useState } from "react";
import { annullaFirma, avviaFirma, getFirmaStatus, getDocumentoFirmato, verifyDelega, generaDocumentoDelega } from "../../api/client";
import type {
  DelegaResult,
  EsitoType,
  FirmaStatus,
  VerdettoCodice,
} from "../../api/types";
import { FirmaBlock } from "../../components/FirmaBlock";

type Step = "input" | "loading" | "result" | "pdf_ready";

// ── Helpers colori (tutti token Indaco) ───────────────────────────────────────

function verdettoBg(stato: VerdettoCodice | undefined): string {
  if (stato === "LEGITTIMATO")     return "var(--indaco-success-light)";
  if (stato === "NON_LEGITTIMATO") return "var(--indaco-danger-light)";
  return "var(--indaco-gray-50)";
}

function verdettoBorderColor(stato: VerdettoCodice | undefined): string {
  if (stato === "LEGITTIMATO")     return "var(--indaco-success)";
  if (stato === "NON_LEGITTIMATO") return "var(--indaco-danger)";
  return "var(--indaco-gray-400)";
}

function verdettoLabel(stato: VerdettoCodice | undefined): string {
  if (stato === "LEGITTIMATO")     return "✅ LEGITTIMATO";
  if (stato === "NON_LEGITTIMATO") return "❌ NON LEGITTIMATO";
  return "—";
}

// ── AnalysisLoading ───────────────────────────────────────────────────────────

const LOADING_STEPS = [
  { icon: "upload_file",  label: "Documento ricevuto" },
  { icon: "find_in_page", label: "Lettura visura camerale" },
  { icon: "policy",       label: "Analisi poteri di firma" },
  { icon: "fact_check",   label: "Redazione verdetto" },
];

function AnalysisLoading({ activeStep, filename }: { activeStep: number; filename: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 24px", maxWidth: 480, margin: "0 auto" }}>
      <span className="material-symbols-outlined" style={{ fontSize: 44, color: "var(--indaco-brand-primary)", marginBottom: "var(--space-4)" }}>
        manage_search
      </span>
      <h2 style={{ fontSize: "var(--font-size-xl)", fontWeight: "var(--font-weight-bold)", color: "var(--indaco-brand-dark)", marginBottom: "var(--space-2)", textAlign: "center", margin: "0 0 var(--space-2)" }}>
        Trusty Sieve sta analizzando la visura
      </h2>
      <p style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-500)", marginBottom: "var(--space-6)", textAlign: "center" }}>
        {filename} — operazione richiede circa 20 secondi
      </p>
      <div style={{ width: "100%" }}>
        {LOADING_STEPS.map((s, i) => {
          const done   = i < activeStep;
          const active = i === activeStep;
          const isLast = i === LOADING_STEPS.length - 1;
          return (
            <div key={i} style={{ display: "flex", gap: "var(--space-4)" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 40, flexShrink: 0 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: done   ? "var(--indaco-success-light)"
                            : active ? "var(--indaco-brand-primary-light)"
                            : "var(--indaco-gray-100)",
                  border: `2px solid ${done   ? "var(--indaco-success)"
                                     : active ? "var(--indaco-brand-primary)"
                                     : "var(--indaco-gray-200)"}`,
                  transition: "all 0.35s ease",
                }}>
                  {done  && <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-success)" }}>check_circle</span>}
                  {active && <div className="spinner" style={{ width: 18, height: 18 }} />}
                  {!done && !active && <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-gray-400)" }}>{s.icon}</span>}
                </div>
                {!isLast && <div style={{ width: 2, height: 24, background: done ? "var(--indaco-success)" : "var(--indaco-gray-200)", transition: "background 0.35s ease" }} />}
              </div>
              <div style={{ paddingTop: 8, paddingBottom: isLast ? 0 : 12, flex: 1 }}>
                <div style={{
                  fontSize: "var(--font-size-sm)",
                  fontWeight: active ? "var(--font-weight-semibold)" : "normal",
                  color: (!done && !active) ? "var(--indaco-gray-400)" : done ? "var(--indaco-gray-700)" : "var(--indaco-brand-dark)",
                  transition: "color 0.35s ease",
                }}>
                  {s.label}
                </div>
                {active && <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-brand-primary)", marginTop: 2 }}>in corso…</div>}
                {done  && <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-success)", marginTop: 2 }}>completato</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── VerdettoBanner ────────────────────────────────────────────────────────────

function VerdettoBanner({ stato, delegante, denominazione, attivita }: {
  stato: VerdettoCodice; delegante: string; denominazione?: string; attivita?: string;
}) {
  const ok = stato === "LEGITTIMATO";
  return (
    <div style={{
      borderRadius: "var(--border-radius-lg)",
      padding: "var(--space-5)",
      marginBottom: "var(--space-4)",
      background: ok ? "var(--indaco-success-light)" : "var(--indaco-danger-light)",
      border: `1.5px solid ${ok ? "var(--indaco-success)" : "var(--indaco-danger)"}`,
      display: "flex", alignItems: "flex-start", gap: "var(--space-4)",
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: 40, color: ok ? "var(--indaco-success)" : "var(--indaco-danger)", flexShrink: 0, marginTop: 2 }}>
        {ok ? "verified" : "gpp_bad"}
      </span>
      <div>
        <div style={{ fontSize: "var(--font-size-xl)", fontWeight: "var(--font-weight-bold)", color: ok ? "var(--indaco-success)" : "var(--indaco-danger)", lineHeight: 1.2 }}>
          {ok ? "Legittimato" : "Non legittimato"}
        </div>
        <div style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-700)", marginTop: "var(--space-1)" }}>
          {delegante}{denominazione ? <> · <strong>{denominazione}</strong></> : null}
        </div>
        {attivita && (
          <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)", marginTop: 3, fontStyle: "italic" }}>
            {attivita}
          </div>
        )}
      </div>
    </div>
  );
}

// ── DeliberazioneTimeline ─────────────────────────────────────────────────────

interface LivelloConfig {
  num: number;
  titolo: string;
  esito?: EsitoType;
  summary: React.ReactNode;
  verbatim?: string;
  note?: string;
}

function DeliberazioneTimeline({ livelli, expanded, onToggle }: {
  livelli: LivelloConfig[];
  expanded: number | null;
  onToggle: (n: number) => void;
}) {
  return (
    <div className="card" style={{ marginBottom: "var(--space-4)", padding: "var(--space-5)" }}>
      <div style={{ fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-bold)", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--indaco-gray-500)", marginBottom: "var(--space-4)" }}>
        Deliberazione
      </div>
      {livelli.map((l, idx) => {
        const ok       = l.esito === "OK";
        const isLast   = idx === livelli.length - 1;
        const hasDetail = !!(l.verbatim || l.note);
        const isOpen   = expanded === l.num;
        const dotColor = l.esito ? (ok ? "var(--indaco-success)" : "var(--indaco-danger)") : "var(--indaco-gray-300)";
        return (
          <div key={l.num} style={{ display: "flex", gap: "var(--space-4)" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 28 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: l.esito ? (ok ? "var(--indaco-success-light)" : "var(--indaco-danger-light)") : "var(--indaco-gray-100)",
                border: `2px solid ${dotColor}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {l.esito && (
                  <span className="material-symbols-outlined" style={{ fontSize: 14, color: dotColor }}>
                    {ok ? "check" : "close"}
                  </span>
                )}
              </div>
              {!isLast && <div style={{ width: 2, flex: 1, minHeight: 20, background: "var(--indaco-gray-200)", margin: "2px 0" }} />}
            </div>
            <div style={{ flex: 1, paddingBottom: isLast ? 0 : "var(--space-5)" }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", cursor: hasDetail ? "pointer" : "default", userSelect: "none" }}
                onClick={() => hasDetail && onToggle(l.num)}
              >
                <span style={{ fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-bold)", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--indaco-gray-400)", minWidth: 18 }}>L{l.num}</span>
                <span style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-900)" }}>{l.titolo}</span>
                {l.esito && <span style={{ marginLeft: "auto", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-semibold)", color: dotColor, flexShrink: 0 }}>{ok ? "✓ OK" : "✗ KO"}</span>}
                {hasDetail && <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-400)", marginLeft: l.esito ? "var(--space-2)" : "auto" }}>{isOpen ? "expand_less" : "expand_more"}</span>}
              </div>
              <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)", marginTop: "var(--space-1)", lineHeight: 1.5 }}>
                {l.summary}
              </div>
              {isOpen && l.verbatim && (
                <pre style={{ marginTop: "var(--space-2)", padding: "var(--space-3)", background: "var(--indaco-gray-50)", border: "1px solid var(--indaco-gray-200)", borderRadius: "var(--border-radius-md)", fontSize: "var(--font-size-xs)", whiteSpace: "pre-wrap", color: "var(--indaco-gray-700)", lineHeight: 1.6, maxHeight: 300, overflowY: "auto" }}>
                  {l.verbatim}
                </pre>
              )}
              {isOpen && l.note && !l.verbatim && (
                <div style={{ marginTop: "var(--space-2)", padding: "var(--space-2) var(--space-3)", background: "var(--indaco-gray-50)", borderRadius: "var(--border-radius-md)", fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-600)" }}>
                  {l.note}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── VisuraDropzone ────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface DropzoneProps {
  file: File | null;
  onChange: (f: File | null) => void;
}

function VisuraDropzone({ file, onChange }: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f && (f.name.endsWith(".xml") || f.name.endsWith(".pdf"))) {
      onChange(f);
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }

  const ext = file?.name.split(".").pop()?.toUpperCase() ?? "";
  const extColor = ext === "XML" ? "var(--indaco-brand-primary)" : "var(--indaco-danger)";

  if (file) {
    return (
      <div className="form-group">
        <label className="form-label">Visura camerale *</label>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-3)",
          padding: "var(--space-3) var(--space-4)",
          border: `2px solid var(--indaco-success)`,
          borderRadius: "var(--border-radius-lg)",
          background: "var(--indaco-success-light)",
        }}>
          <div style={{
            flexShrink: 0,
            width: 38, height: 46,
            borderRadius: "var(--border-radius-md)",
            background: extColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--indaco-white)",
            fontSize: "var(--font-size-xs)",
            fontWeight: "var(--font-weight-bold)",
            letterSpacing: "0.05em",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, marginBottom: 2 }}>
              {ext === "XML" ? "code" : "picture_as_pdf"}
            </span>
            {ext}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="truncate fw-semibold" style={{ color: "var(--indaco-gray-900)" }}>{file.name}</div>
            <div className="text-xs text-muted" style={{ marginTop: "var(--space-1)" }}>{formatBytes(file.size)}</div>
          </div>
          <button
            type="button"
            onClick={() => { onChange(null); if (inputRef.current) inputRef.current.value = ""; }}
            title="Rimuovi file"
            style={{
              flexShrink: 0,
              width: 28, height: 28,
              borderRadius: "50%",
              border: "none",
              background: "var(--indaco-danger-light)",
              color: "var(--indaco-danger)",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
          </button>
        </div>
        <input ref={inputRef} type="file" accept=".xml,.pdf" style={{ display: "none" }}
          onChange={e => onChange(e.target.files?.[0] ?? null)} />
      </div>
    );
  }

  return (
    <div className="form-group">
      <label className="form-label">
        Visura camerale * <span className="text-muted" style={{ fontWeight: "var(--font-weight-regular)" }}>.xml o .pdf</span>
      </label>
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={e => e.key === "Enter" && inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false); }}
        style={{
          cursor: "pointer",
          border: `2px dashed ${isDragging ? "var(--indaco-brand-primary)" : "var(--indaco-gray-300)"}`,
          borderRadius: "var(--border-radius-xl)",
          background: isDragging ? "var(--indaco-brand-primary-light)" : "var(--indaco-gray-50)",
          padding: "var(--space-8) var(--space-6)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--space-2)",
          transition: "border-color var(--transition-fast), background var(--transition-fast)",
          userSelect: "none",
          outline: "none",
        }}
        onFocus={e => (e.currentTarget.style.boxShadow = "0 0 0 3px rgba(var(--indaco-brand-primary-rgb), 0.15)")}
        onBlur={e => (e.currentTarget.style.boxShadow = "none")}
      >
        <span
          className="material-symbols-outlined"
          style={{
            fontSize: 40,
            color: isDragging ? "var(--indaco-brand-primary)" : "var(--indaco-gray-400)",
            transition: "color var(--transition-fast)",
          }}
        >
          upload_file
        </span>
        <div style={{ textAlign: "center" }}>
          <div style={{
            fontSize: "var(--font-size-base)",
            fontWeight: "var(--font-weight-semibold)",
            color: isDragging ? "var(--indaco-brand-primary)" : "var(--indaco-gray-700)",
          }}>
            {isDragging ? "Rilascia qui" : "Trascina la visura qui"}
          </div>
          <div className="text-sm text-muted" style={{ marginTop: "var(--space-1)" }}>
            oppure <span style={{ color: "var(--indaco-brand-primary)", textDecoration: "underline" }}>clicca per sfogliare</span>
          </div>
        </div>
        <div style={{ marginTop: "var(--space-1)", display: "flex", gap: "var(--space-2)" }}>
          <span className="badge badge-info">XML</span>
          <span className="badge badge-danger">PDF</span>
        </div>
      </div>
      <input ref={inputRef} type="file" accept=".xml,.pdf" style={{ display: "none" }}
        onChange={e => onChange(e.target.files?.[0] ?? null)} />
    </div>
  );
}


// ── Componente principale ─────────────────────────────────────────────────────

export function DelegheVerify() {
  const [step, setStep] = useState<Step>("input");
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [deleganteNome, setDeleganteNome] = useState("");
  const [deleganteCognome, setDeleganteCognome] = useState("");
  const [deleganteEmail, setDeleganteEmail] = useState("");
  const [deleganteTelefono, setDeleganteTelefono] = useState("");
  const [deleganteCf, setDeleganteCf] = useState("");
  const [deleganteDataNascita, setDeleganteDataNascita] = useState("");
  const [delegatoNome, setDelegatoNome] = useState("");
  const [delegatoCognome, setDelegatoCognome] = useState("");
  const [delegatoEmail, setDelegatoEmail] = useState("");
  const [delegatoTelefono, setDelegatoTelefono] = useState("");
  const [delegatoCf, setDelegatoCf] = useState("");
  const [delegatoDataNascita, setDelegatoDataNascita] = useState("");
  const [attivita, setAttivita] = useState("");
  const [visura, setVisura] = useState<File | null>(null);

  // Result
  const [result, setResult] = useState<DelegaResult | null>(null);

  // PDF
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Firma
  const [firmaStatus, setFirmaStatus] = useState<FirmaStatus | null>(null);
  const [firmaLoading, setFirmaLoading] = useState(false);
  const [annullaLoading, setAnnullaLoading] = useState(false);
  const [firmatoLoading, setFirmatoLoading] = useState(false);
  const firmaPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Loading animation
  const [loadingStep, setLoadingStep] = useState(0);
  // Result: livello espanso nella timeline
  const [expandedLevel, setExpandedLevel] = useState<number | null>(null);

  async function handleVerifica() {
    if (!deleganteNome.trim() || !deleganteCognome.trim()) { setError("Inserisci nome e cognome del delegante."); return; }
    if (!deleganteEmail.trim()) { setError("Inserisci l'email del delegante."); return; }
    if (!deleganteCf.trim()) { setError("Inserisci il codice fiscale del delegante."); return; }
    if (!deleganteDataNascita.trim()) { setError("Inserisci la data di nascita del delegante."); return; }
    if (!delegatoNome.trim() || !delegatoCognome.trim()) { setError("Inserisci nome e cognome del delegato."); return; }
    if (!delegatoEmail.trim()) { setError("Inserisci l'email del delegato."); return; }
    if (!delegatoTelefono.trim()) { setError("Inserisci il telefono del delegato."); return; }
    if (deleganteTelefono.trim() && delegatoTelefono.trim() === deleganteTelefono.trim()) {
      setError("Delegante e delegato devono avere numeri di telefono diversi per la firma digitale.");
      return;
    }
    if (!delegatoCf.trim()) { setError("Inserisci il codice fiscale del delegato."); return; }
    if (!delegatoDataNascita.trim()) { setError("Inserisci la data di nascita del delegato."); return; }
    if (!attivita.trim()) { setError("Descrivi l'attività da delegare."); return; }
    if (!visura) { setError("Carica la visura camerale (XML o PDF)."); return; }

    setError(null);
    setStep("loading");
    try {
      const res = await verifyDelega({
        delegante_nome: deleganteNome.trim(),
        delegante_cognome: deleganteCognome.trim(),
        delegante_email: deleganteEmail.trim(),
        delegante_telefono: deleganteTelefono.trim(),
        delegante_cf: deleganteCf.trim().toUpperCase(),
        delegante_data_nascita: deleganteDataNascita.trim(),
        delegato_nome: delegatoNome.trim(),
        delegato_cognome: delegatoCognome.trim(),
        delegato_email: delegatoEmail.trim(),
        delegato_telefono: delegatoTelefono.trim(),
        delegato_cf: delegatoCf.trim().toUpperCase(),
        delegato_data_nascita: delegatoDataNascita.trim(),
        attivita: attivita.trim(),
        visura,
      });
      // Completa l'animazione: forza step 3 e aspetta 1.2s per mostrarlo
      setLoadingStep(3);
      await new Promise(r => setTimeout(r, 1200));
      setExpandedLevel(null);
      setResult(res);
      setStep("result");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Errore sconosciuto");
      setStep("input");
    }
  }

  async function handleGeneraPdf() {
    if (!result) return;
    setIsGeneratingPdf(true);
    try {
      const blob = await generaDocumentoDelega(result.check_id);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      // Recupero lo stato firma corrente (se già avviato in precedenza)
      try {
        const fs = await getFirmaStatus(result.check_id);
        setFirmaStatus(fs);
      } catch { /* primo accesso: nessun stato firma ancora */ }
      setStep("pdf_ready");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Errore generazione PDF");
    } finally {
      setIsGeneratingPdf(false);
    }
  }

  async function handleAvviaFirma() {
    if (!result) return;
    setFirmaLoading(true);
    setError(null);
    try {
      const fs = await avviaFirma(result.check_id);
      setFirmaStatus(fs);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Errore avvio processo di firma");
    } finally {
      setFirmaLoading(false);
    }
  }

  async function handleAnnullaFirma() {
    if (!result) return;
    setAnnullaLoading(true);
    setError(null);
    try {
      await annullaFirma(result.check_id);
      setFirmaStatus(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Errore annullamento firma");
    } finally {
      setAnnullaLoading(false);
    }
  }

  async function handleDownloadFirmato() {
    if (!result) return;
    setFirmatoLoading(true);
    setError(null);
    try {
      const blob = await getDocumentoFirmato(result.check_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `procura_firmata_${result.check_id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Errore download documento firmato");
    } finally {
      setFirmatoLoading(false);
    }
  }

  // Polling stato firma ogni 15s quando è attivo
  useEffect(() => {
    const isActive =
      firmaStatus?.firma_status === "attesa_delegante" ||
      firmaStatus?.firma_status === "attesa_delegato";

    if (!isActive || !result) {
      if (firmaPollingRef.current) {
        clearInterval(firmaPollingRef.current);
        firmaPollingRef.current = null;
      }
      return;
    }

    firmaPollingRef.current = setInterval(async () => {
      try {
        const fs = await getFirmaStatus(result.check_id);
        setFirmaStatus(fs);
      } catch { /* non-fatale */ }
    }, 15_000);

    return () => {
      if (firmaPollingRef.current) clearInterval(firmaPollingRef.current);
    };
  }, [firmaStatus?.firma_status, result?.check_id]);

  // Avanza automaticamente loadingStep 0→1 e 1→2 mentre aspettiamo la risposta API.
  // Lo step 3 (Redazione verdetto) viene triggerato da handleVerifica quando la call torna.
  useEffect(() => {
    if (step !== "loading") { setLoadingStep(0); return; }
    const t1 = setTimeout(() => setLoadingStep(s => Math.max(s, 1)), 1500);
    const t2 = setTimeout(() => setLoadingStep(s => Math.max(s, 2)), 9000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [step]);

  function handleReset() {
    if (firmaPollingRef.current) clearInterval(firmaPollingRef.current);
    setStep("input");
    setDeleganteNome(""); setDeleganteCognome("");
    setDeleganteEmail(""); setDeleganteTelefono("");
    setDeleganteCf(""); setDeleganteDataNascita("");
    setDelegatoNome(""); setDelegatoCognome("");
    setDelegatoEmail(""); setDelegatoTelefono("");
    setDelegatoCf(""); setDelegatoDataNascita("");
    setAttivita(""); setVisura(null);
    setResult(null); setPdfUrl(null);
    setFirmaStatus(null);
    setError(null);
    setLoadingStep(0);
    setExpandedLevel(null);
  }

  const phonesMatch = deleganteTelefono.trim() !== "" &&
    delegatoTelefono.trim() !== "" &&
    deleganteTelefono.trim() === delegatoTelefono.trim();

  const canSubmit =
    deleganteNome.trim() !== "" &&
    deleganteCognome.trim() !== "" &&
    deleganteEmail.trim() !== "" &&
    deleganteCf.trim() !== "" &&
    deleganteDataNascita.trim() !== "" &&
    delegatoNome.trim() !== "" &&
    delegatoCognome.trim() !== "" &&
    delegatoEmail.trim() !== "" &&
    delegatoTelefono.trim() !== "" &&
    delegatoCf.trim() !== "" &&
    delegatoDataNascita.trim() !== "" &&
    attivita.trim() !== "" &&
    visura !== null &&
    !phonesMatch;

  const row2: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "var(--space-3)",
    marginBottom: "var(--space-3)",
  };
  const sectionLabel: React.CSSProperties = {
    fontSize: "var(--font-size-xs)",
    fontWeight: "var(--font-weight-bold)",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "var(--indaco-gray-500)",
    marginBottom: "var(--space-2)",
    marginTop: "var(--space-4)",
  };

  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: "var(--space-6)" }}>
        <span className="material-symbols-outlined" style={{ marginRight: "var(--space-2)" }}>verified_user</span>
        Verifica Deleghe
      </h1>

      {error && (
        <div className="card toast-error" style={{ marginBottom: "var(--space-4)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: "var(--space-1)" }}>error</span>
          {error}
        </div>
      )}

      {/* ── Step: input ── */}
      {step === "input" && (
        <div className="card" style={{ maxWidth: 680 }}>
          <h2 style={{ marginBottom: "var(--space-5)" }}>Nuova verifica</h2>

          <p style={sectionLabel}>Delegante (chi firma per primo)</p>
          <div style={row2}>
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input className="form-input" type="text" placeholder="Mario" value={deleganteNome} onChange={e => setDeleganteNome(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Cognome *</label>
              <input className="form-input" type="text" placeholder="Rossi" value={deleganteCognome} onChange={e => setDeleganteCognome(e.target.value)} />
            </div>
          </div>
          <div style={row2}>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input className="form-input" type="email" placeholder="mario.rossi@azienda.it" value={deleganteEmail} onChange={e => setDeleganteEmail(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Telefono WA (per firma)</label>
              <input className="form-input" type="text" placeholder="+39 333 1234567" value={deleganteTelefono} onChange={e => setDeleganteTelefono(e.target.value)} />
            </div>
          </div>
          <div style={row2}>
            <div className="form-group">
              <label className="form-label">Codice Fiscale *</label>
              <input className="form-input" type="text" placeholder="RSSMRA85M01H501Z" style={{ textTransform: "uppercase" }} value={deleganteCf} onChange={e => setDeleganteCf(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Data di Nascita *</label>
              <input className="form-input" type="text" placeholder="01/01/1985" value={deleganteDataNascita} onChange={e => setDeleganteDataNascita(e.target.value)} />
            </div>
          </div>

          <p style={sectionLabel}>Delegato (chi firma per secondo)</p>
          <div style={row2}>
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input className="form-input" type="text" placeholder="Luigi" value={delegatoNome} onChange={e => setDelegatoNome(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Cognome *</label>
              <input className="form-input" type="text" placeholder="Verdi" value={delegatoCognome} onChange={e => setDelegatoCognome(e.target.value)} />
            </div>
          </div>
          <div style={row2}>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input className="form-input" type="email" placeholder="luigi.verdi@azienda.it" value={delegatoEmail} onChange={e => setDelegatoEmail(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Telefono WA (per firma) *</label>
              <input className="form-input" type="text" placeholder="+39 333 1234567" value={delegatoTelefono} onChange={e => setDelegatoTelefono(e.target.value)} />
            </div>
          </div>
          <div style={row2}>
            <div className="form-group">
              <label className="form-label">Codice Fiscale *</label>
              <input className="form-input" type="text" placeholder="VRDLGI90H01H501Y" style={{ textTransform: "uppercase" }} value={delegatoCf} onChange={e => setDelegatoCf(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Data di Nascita *</label>
              <input className="form-input" type="text" placeholder="01/01/1990" value={delegatoDataNascita} onChange={e => setDelegatoDataNascita(e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Attività da delegare *</label>
            <textarea
              className="form-textarea"
              placeholder="Es. stipulare contratti di fornitura di gas naturale con Acquirente Unico S.p.A."
              value={attivita}
              onChange={e => setAttivita(e.target.value)}
            />
          </div>

          <VisuraDropzone file={visura} onChange={setVisura} />

          {phonesMatch && (
            <div className="alert alert-warning" style={{ marginBottom: "var(--space-3)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, flexShrink: 0 }}>warning</span>
              Il telefono del delegante e del delegato è lo stesso — per la firma digitale sono richiesti due numeri diversi.
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              className="btn btn-primary"
              onClick={handleVerifica}
              disabled={!canSubmit}
              title={!canSubmit ? "Compila tutti i campi e carica la visura per procedere" : undefined}
            >
              <span className="material-symbols-outlined">gavel</span>
              Verifica
            </button>
          </div>
        </div>
      )}

      {/* ── Step: loading ── */}
      {step === "loading" && (
        <AnalysisLoading activeStep={loadingStep} filename={visura?.name ?? "visura"} />
      )}

      {/* ── Step: result ── */}
      {step === "result" && result && (
        <div style={{ maxWidth: 720 }}>

          {/* Warnings analisi */}
          {result.warnings?.map((w, i) => (
            <div key={i} className="alert alert-warning" style={{ marginBottom: "var(--space-3)", alignItems: "flex-start" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>warning</span>
              <span style={{ fontSize: "var(--font-size-sm)" }}>{w}</span>
            </div>
          ))}

          {/* Hero verdetto */}
          <VerdettoBanner
            stato={result.verdetto.stato}
            delegante={`${result.delegante_nome ?? ""} ${result.delegante_cognome ?? ""}`.trim()}
            denominazione={result.company_info?.denominazione}
            attivita={attivita}
          />

          {/* Timeline L1 / L2 / L3 */}
          <DeliberazioneTimeline
            expanded={expandedLevel}
            onToggle={n => setExpandedLevel(expandedLevel === n ? null : n)}
            livelli={[
              {
                num: 1,
                titolo: "Società e soggetto",
                esito: result.livello_1?.esito,
                summary: result.livello_1 ? (
                  <>
                    {result.livello_1.societa_attiva ? "Società attiva" : "Società non attiva"}
                    {result.livello_1.in_liquidazione ? " · in liquidazione" : ""}
                    {result.livello_1.nome_in_visura ? ` · ${result.livello_1.nome_in_visura}` : ""}
                    {result.livello_1.sezione_trovata ? ` (${result.livello_1.sezione_trovata})` : ""}
                  </>
                ) : "—",
                note: result.livello_1?.note,
              },
              {
                num: 2,
                titolo: "Poteri di firma",
                esito: result.livello_2?.esito,
                summary: result.livello_2 ? (
                  <>
                    {result.livello_2.carica ?? "—"}
                    {result.livello_2.modalita_firma ? ` · ${result.livello_2.modalita_firma}` : ""}
                    {result.livello_2.regola_applicata ? ` · ${result.livello_2.regola_applicata}` : ""}
                    {result.livello_2.data_cessazione
                      ? <span style={{ color: "var(--indaco-danger)" }}> · carica cessata {result.livello_2.data_cessazione}</span>
                      : null}
                  </>
                ) : "—",
                verbatim: result.livello_2?.poteri_verbatim,
                note: result.livello_2?.note,
              },
              {
                num: 3,
                titolo: "Coerenza della delega",
                esito: result.livello_3?.esito,
                summary: result.livello_3 ? (
                  <>
                    {result.livello_3.oggetto_compatibile ? "Oggetto compatibile" : "Oggetto non compatibile"}
                    {" · "}
                    {result.livello_3.non_eccede_poteri ? "poteri non ecceduti" : "poteri ecceduti"}
                  </>
                ) : "—",
                note: result.livello_3?.note,
              },
            ]}
          />

          {/* Ragionamento del modello */}
          {result.verdetto.reasoning && (
            <div className="card" style={{ marginBottom: "var(--space-4)", background: "var(--indaco-gray-50)", border: "1px solid var(--indaco-gray-200)" }}>
              <div style={{ fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-bold)", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--indaco-gray-500)", marginBottom: "var(--space-3)" }}>
                Ragionamento del modello
              </div>
              <p style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-700)", lineHeight: 1.65, margin: 0 }}>
                {result.verdetto.reasoning}
              </p>
            </div>
          )}

          {/* AI cost summary */}
          {result.ai_steps.length > 0 && (
            <details className="card" style={{ marginBottom: "var(--space-3)" }}>
              <summary className="text-sm fw-medium" style={{ cursor: "pointer", color: "var(--indaco-gray-500)", fontSize: "var(--font-size-xs)" }}>
                Dettagli AI — ${result.ai_steps.reduce((a, s) => a + s.cost_usd, 0).toFixed(4)} · {result.ai_steps.reduce((a, s) => a + s.tokens, 0).toLocaleString()} token · {Math.round(result.ai_steps.reduce((a, s) => a + s.latency_ms, 0) / 1000)}s
              </summary>
              <table style={{ width: "100%", marginTop: "var(--space-2)", borderCollapse: "collapse", fontSize: "var(--font-size-xs)" }}>
                <thead><tr style={{ borderBottom: `1px solid var(--indaco-gray-200)` }}>
                  <th style={{ textAlign: "left", padding: "var(--space-1) var(--space-2)", color: "var(--indaco-gray-500)" }}>Step</th>
                  <th style={{ textAlign: "right", padding: "var(--space-1) var(--space-2)", color: "var(--indaco-gray-500)" }}>Token</th>
                  <th style={{ textAlign: "right", padding: "var(--space-1) var(--space-2)", color: "var(--indaco-gray-500)" }}>ms</th>
                  <th style={{ textAlign: "right", padding: "var(--space-1) var(--space-2)", color: "var(--indaco-gray-500)" }}>$</th>
                </tr></thead>
                <tbody>{result.ai_steps.map((s, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid var(--indaco-gray-100)` }}>
                    <td style={{ padding: "var(--space-1) var(--space-2)", color: "var(--indaco-gray-700)" }}>{s.step}</td>
                    <td style={{ textAlign: "right", padding: "var(--space-1) var(--space-2)" }}>{s.tokens.toLocaleString()}</td>
                    <td style={{ textAlign: "right", padding: "var(--space-1) var(--space-2)" }}>{Math.round(s.latency_ms)}</td>
                    <td style={{ textAlign: "right", padding: "var(--space-1) var(--space-2)" }}>{s.cost_usd.toFixed(5)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </details>
          )}

          {/* Azioni */}
          <div className="flex gap-4" style={{ flexWrap: "wrap" }}>
            {result.verdetto.stato === "LEGITTIMATO" && (
              <button className="btn btn-primary" onClick={handleGeneraPdf} disabled={isGeneratingPdf}>
                {isGeneratingPdf
                  ? <><span className="spinner" style={{ width: 14, height: 14 }} />Generazione…</>
                  : <><span className="material-symbols-outlined">picture_as_pdf</span>Genera Documento Delega</>
                }
              </button>
            )}
            <button className="btn btn-outline" onClick={handleReset}>
              <span className="material-symbols-outlined">add</span>
              Nuova verifica
            </button>
          </div>
        </div>
      )}

      {/* ── Step: pdf_ready ── */}
      {step === "pdf_ready" && pdfUrl && (
        <div style={{ maxWidth: 720 }}>
          <div className="card" style={{ marginBottom: "var(--space-4)", background: "var(--indaco-success-light)", border: `1px solid var(--indaco-success)` }}>
            <strong style={{ color: "var(--indaco-success)" }}>✅ Documento generato con successo</strong>
          </div>
          <iframe
            src={pdfUrl}
            style={{ width: "100%", height: 600, border: `1px solid var(--border-color)`, borderRadius: "var(--border-radius-lg)", marginBottom: "var(--space-4)" }}
            title="Procura speciale"
          />
          <div className="flex gap-4" style={{ marginBottom: "var(--space-6)" }}>
            <a href={pdfUrl} download={`procura_${result?.check_id?.slice(0, 8) ?? "delega"}.pdf`} className="btn btn-primary">
              <span className="material-symbols-outlined">download</span>
              Scarica PDF
            </a>
            <button className="btn btn-outline" onClick={handleReset}>
              <span className="material-symbols-outlined">add</span>
              Nuova verifica
            </button>
          </div>

          {/* ── Processo di firma ── */}
          <FirmaBlock
            firmaStatus={firmaStatus?.firma_status ?? null}
            deleganteLabel={`${result?.delegante_nome ?? deleganteNome} ${result?.delegante_cognome ?? deleganteCognome}`.trim()}
            delegatoLabel={`${delegatoNome} ${delegatoCognome}`.trim()}
            waNumber={firmaStatus?.wa_number}
            firmaCompletataAt={firmaStatus?.firma_completata_at}
            firmaLoading={firmaLoading}
            annullaLoading={annullaLoading}
            firmatoLoading={firmatoLoading}
            onAvvia={handleAvviaFirma}
            onRetry={handleAvviaFirma}
            onAnnulla={handleAnnullaFirma}
            onDownloadFirmato={handleDownloadFirmato}
          />
        </div>
      )}
    </div>
  );
}
